import { LOCALE_CODES, type Locale, localeMeta } from '@upweo/i18n';

import { useI18n } from '../i18n-context.js';
import { cn } from '../lib/cn.js';

/**
 * The language switcher.
 *
 * Each option is labelled in ITS OWN language — "Deutsch", not "German". A
 * person looking for their language is, by definition, someone who may not read
 * the one currently on screen, so translating the list into the active language
 * hides exactly the entry they need.
 *
 * A plain `<select>` rather than a custom dropdown: it is keyboard accessible
 * everywhere, and on mobile it opens the native picker, which is easier to use
 * than anything we would build.
 */
export const LanguagePicker = ({
  allowed = LOCALE_CODES,
  className,
  compact = false,
}: {
  /** Restrict to the languages a store actually offers. */
  allowed?: readonly Locale[];
  className?: string;
  /** Drop the visible label where space is tight, keeping it for screen readers. */
  compact?: boolean;
}): React.JSX.Element | null => {
  const { locale, setLocale, t } = useI18n();

  // One language is not a choice; showing a picker with a single option is
  // clutter that implies an option the merchant did not enable.
  if (allowed.length < 2) {
    return null;
  }

  const select = (
    <select
      aria-label={t('common.language')}
      value={locale}
      onChange={(event) => {
        setLocale(event.target.value as Locale);
      }}
      className={cn(
        'h-8 rounded-md border border-input bg-background px-2 text-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      {allowed.map((code) => (
        // `lang` on the option so a screen reader pronounces "Français" in
        // French rather than reading it as English.
        <option key={code} value={code} lang={code}>
          {localeMeta(code).nativeName}
        </option>
      ))}
    </select>
  );

  if (compact) {
    return select;
  }

  return (
    <label className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">{t('common.language')}</span>
      {select}
    </label>
  );
};
